import { schema } from 'prose-core'
import { MarkType } from 'prosemirror-model'
import { NodeSelection } from 'prosemirror-state'
import { type EditorView } from 'prosemirror-view'
import { useMemo, useState } from 'react'
import {
  determineFontAttribute,
  getFontAttributeConfig,
} from '../Toolbar/components/Format/components/utils/determineFontAttribute'

export function useFontAttrInSelection(view: EditorView, markType: MarkType) {
  const state = view.state
  const [forceRerender, setForceRerender] = useState<boolean>(false)

  return useMemo(() => {
    const fontAttributeValue = getFontAttrValue(view, markType)

    // NOTE: sometimes the dom node is not yet attached and thus the font
    // attr can't be read from the computed style. In that case try again
    // after this render.
    if (!Array.isArray(fontAttributeValue) && fontAttributeValue.has(null)) {
      setTimeout(() => setForceRerender(!forceRerender))

      return []
    }

    return Array.from(fontAttributeValue)
    // NOTE: There's a bug where the view doesn't update, so we actually
    // sometimes get an out of date doc if we call view.state.doc, hence we
    // rely on the state to update here, but use the view everywhere inside.
    // This potentially leads to wrong values, but since the view eventually
    // catches up it resolves itself
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceRerender, markType, state, view])
}

const removeQuotesFromFontFamily = (fontAttribute: string, markType: MarkType): string => {
  // Fix bug in Firefox where font-family includes double quotes (EDI-604)
  if (fontAttribute && markType === schema.marks.font_family) {
    // only return first font found in families
    return fontAttribute.replace(/"/g, '').split(',')[0]
  }

  return fontAttribute
}

export const getFontAttrValue = (view: EditorView, markType: MarkType) => {
  const { selection } = view.state

  if (selection instanceof NodeSelection) {
    const fallbackAttr = determineFontAttribute(view, selection.node, selection.from, markType)
    if (markType === schema.marks.colored) {
      return [selection.node.attrs.fontColor ?? fallbackAttr]
    }
    if (markType === schema.marks.font_family) {
      return [selection.node.attrs.fontFamily ?? removeQuotesFromFontFamily(fallbackAttr, markType)]
    }
    if (markType === schema.marks.font_weight) {
      return [selection.node.attrs.fontWeight ?? fallbackAttr]
    }
    if (markType === schema.marks.font_size) {
      return [selection.node.attrs.fontSize ?? fallbackAttr]
    }
    return []
  }
  if (view.state.storedMarks != null && view.state.storedMarks.length === 0) {
    // As per https://discuss.prosemirror.net/t/understanding-togglemark-and-storedmarks/2682
    // storedMarks == null means "don't do anything special for the next insertion" (which mean inherit the marks
    // around the cursor)
    // storedMarks === [] means "make sure the next insertion doesn't have any marks set".
    // storedMarks === [mark1, mark2] means "only apply mark1 and mark2 to the next insertion".
    return []
  }

  const fontAttributeValue: Set<any | null> = new Set()

  const { attrName } = getFontAttributeConfig(markType)
  const storedFontAttribute = markType.isInSet(view.state.storedMarks ?? [])

  if (storedFontAttribute) {
    return [storedFontAttribute.attrs[attrName]]
  }

  for (let {
    $from: { pos: from },
    $to,
  } of selection.ranges) {
    view.state.doc.nodesBetween(from, $to.pos, (node, pos) => {
      if (!node.isText) {
        return
      }

      fontAttributeValue.add(removeQuotesFromFontFamily(determineFontAttribute(view, node, pos, markType), markType))
    })

    if (fontAttributeValue.size === 0) {
      const node = $to.nodeBefore || $to.nodeAfter || $to.node()

      fontAttributeValue.add(
        removeQuotesFromFontFamily(determineFontAttribute(view, node, $to.pos, markType), markType),
      )
    }
  }

  return fontAttributeValue
}

export const getIntFontAttrValue = (view: EditorView, markType: MarkType) => {
  const fontAttributeValue: Set<number> = new Set()
  const {
    state: { selection },
  } = view
  if (
    markType === schema.marks.font_size &&
    selection instanceof NodeSelection &&
    typeof selection.node.attrs.fontSize === 'number'
  ) {
    fontAttributeValue.add(selection.node.attrs.fontSize)
    return fontAttributeValue
  }
  for (const elem of getFontAttrValue(view, markType)) {
    const val = parseInt(elem)
    if (!isNaN(val)) {
      fontAttributeValue.add(val)
    }
  }
  return fontAttributeValue
}
